import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Eye, FileSignature, Phone, DollarSign } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './MeusClientes.css';

const normalizar = (valor) =>
  String(valor || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export default function MeusClientes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregarClientes = async () => {
    setCarregando(true);
    setErro('');

    try {
      const token = localStorage.getItem('token');
      const resposta = await fetch('/api/clients/meus', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const dados = await resposta.json();

      if (!resposta.ok) {
        throw new Error(dados.error || 'Não foi possível carregar os clientes');
      }

      setClientes(dados.clientes || []);
    } catch (err) {
      setErro(err.message);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarClientes();
  }, []);

  const clientesFiltrados = useMemo(() => {
    const termo = normalizar(busca.trim());

    if (!termo) return clientes;

    return clientes.filter((cliente) => {
      const nome = normalizar(cliente.full_name);
      const documento = normalizar(cliente.cpf || cliente.cnpj);

      return nome.includes(termo) || documento.includes(termo);
    });
  }, [clientes, busca]);

  const abrirCliente = (cliente) => {
    navigate('/clients', {
      state: {
        client: { ...cliente, _source: 'cpfl' },
        openEdit: true
      }
    });
  };

  const modalidade = (cliente) => {
    if (cliente.payment_type === 'cpfl') return 'CPFL';
    return cliente.payment_type || '—';
  };

  const documento = (cliente) =>
    cliente.cnpj || cliente.cpf || '—';

  const status = (valor) =>
    valor || 'PENDENTE';

  const statusCobranca = (valor) =>
    String(valor || '').toUpperCase() === 'RASCUNHO'
      ? 'PENDENTE'
      : (valor || 'PENDENTE');

  const classeStatus = (valor) =>
    String(valor || 'PENDENTE')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-');

  const normalizarPerfil = (valor) =>
    String(valor || '')
      .toLowerCase()
      .replace(/[áàãâä]/g, 'a')
      .replace(/[éèêë]/g, 'e')
      .replace(/[íìîï]/g, 'i')
      .replace(/[óòõôö]/g, 'o')
      .replace(/[úùûü]/g, 'u')
      .replace(/ç/g, 'c')
      .replace(/[_-]/g, ' ')
      .trim();

  const rolesBrutos = [
    ...(Array.isArray(user?.roles)
      ? user.roles
      : (user?.roles ? [user.roles] : [])),
    user?.role,
    user?.cargo,
    user?.setor,
    user?.full_name,
    user?.email,
    ...(user?.setores && typeof user.setores === 'object'
      ? Object.keys(user.setores)
      : [])
  ];

  const perfisUsuario = rolesBrutos.map(normalizarPerfil);

  const ehPosVenda = perfisUsuario.some((perfil) =>
    perfil.includes('pos venda') ||
    perfil.includes('posvenda') ||
    perfil === 'julia' ||
    perfil.includes('aproveitamentosou@gmail.com')
  );

  const responderPosVenda = async (cliente, feedback) => {
    const token = localStorage.getItem('token');

    if (feedback === 'Confirmado e feito ligação') {
      const confirmou = window.confirm(
        `Deseja confirmar que a ligação com ${cliente.full_name || 'este cliente'} foi realizada?`
      );

      if (!confirmou) return;
    }

    let feedbackFinal = feedback;

    if (feedback === 'Outro motivo') {
      const motivo = window.prompt('Digite o motivo da devolutiva:');

      if (!motivo || !motivo.trim()) return;

      feedbackFinal = motivo.trim();
    }

    try {
      const resposta = await fetch(`/api/clients/${cliente.id}/responder-pos-venda`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ feedback: feedbackFinal })
      });

      const dados = await resposta.json();

      if (!resposta.ok) {
        throw new Error(dados.mensagem || 'Não foi possível registrar a devolutiva.');
      }

      alert(dados.mensagem || 'Devolutiva registrada.');
      await carregarClientes();
    } catch (error) {
      alert(error.message);
    }
  };

  const abrirAssinatura = (cliente) => {
    if (!cliente.clicksign_envelope_id) {
      alert('Este cliente ainda não possui um documento ClickSign vinculado.');
      return;
    }

    const token = localStorage.getItem('token');
    const url =
      `/api/clicksign-envios/${encodeURIComponent(cliente.clicksign_envelope_id)}/arquivo` +
      `?tipo=assinado&token=${encodeURIComponent(token || '')}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const solicitarPosVenda = async (cliente) => {
    const token = localStorage.getItem('token');

    try {
      const resposta = await fetch(`/api/clients/${cliente.id}/solicitar-pos-venda`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const dados = await resposta.json();

      if (!resposta.ok) {
        throw new Error(dados.mensagem || 'Não foi possível solicitar o Pós-venda.');
      }

      alert(dados.mensagem || 'Solicitação enviada ao Pós-venda.');
      await carregarClientes();
    } catch (error) {
      alert(error.message);
    }
  };

  const abrirSimulacaoCobranca = (cliente) => {
    alert(
      [
        'Cobrança ainda em modo de simulação.',
        '',
        `Cliente: ${cliente.full_name || '—'}`,
        `Plano: ${cliente.plan_name || '—'}`,
        `Valor: ${cliente.plan_value ?? '—'}`,
        '',
        'Nenhuma cobrança foi enviada para a CPFL.'
      ].join('\\n')
    );
  };

  const podeSolicitarPosVenda = (cliente) => {
    const clicksign = String(cliente.clicksign_status || '').toUpperCase();
    const posVenda = String(cliente.pos_venda_status || '').toUpperCase();

    return clicksign === 'ASSINADO' &&
      !['ENVIADO', 'PENDENTE', 'CONFIRMADO'].includes(posVenda);
  };

  return (
    <div className="meus-clientes-page">
      <div className="meus-clientes-heading">
        <div>
          <h1>Meus Clientes</h1>
          <p>Buscar e visualizar dados de clientes existentes</p>
        </div>
      </div>

      <div className="meus-clientes-toolbar">
        <div className="meus-clientes-search">
          <Search size={17} />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou CPF/CNPJ"
          />
        </div>

        <button
          className="meus-clientes-add"
          onClick={() => navigate('/meus-clientes/cadastro')}
        >
          <Plus size={17} />
          Adicionar cliente
        </button>
      </div>

      {carregando && (
        <div className="meus-clientes-feedback">Carregando clientes...</div>
      )}

      {!carregando && erro && (
        <div className="meus-clientes-feedback error">{erro}</div>
      )}

      {!carregando && !erro && (
        <div className="meus-clientes-table-wrap">
          <table className="meus-clientes-table">
            <thead>
              <tr>
                <th>CLIENTE</th>
                <th>CPF / CNPJ</th>
                <th>MODALIDADE</th>
                <th>CLICKSIGN</th>
                <th>POS VENDA</th>
                <th>COBRANÇA</th>
                <th>STATUS</th>
                <th>AÇÕES</th>
              </tr>
            </thead>

            <tbody>
              {clientesFiltrados.map((cliente) => (
                <tr key={cliente.id}>
                  <td>
                    <div className="meus-cliente-identidade">
                      <span className="meus-cliente-avatar">
                        {(cliente.full_name || '?').charAt(0).toUpperCase()}
                      </span>
                      <strong>{cliente.full_name || '—'}</strong>
                    </div>
                  </td>

                  <td>{documento(cliente)}</td>

                  <td>
                    <span className="meus-modalidade">
                      {modalidade(cliente)}
                    </span>
                  </td>

                  <td>
                    <span className={`meus-status ${classeStatus(cliente.clicksign_status)}`}>
                      {status(cliente.clicksign_status)}
                    </span>
                  </td>

                  <td>
                    <span className={`meus-status ${classeStatus(cliente.pos_venda_status)}`}>
                      {status(cliente.pos_venda_status)}
                    </span>
                  </td>

                  <td>
                    <span className={`meus-status ${classeStatus(cliente.cobranca_status)}`}>
                      {statusCobranca(cliente.cobranca_status)}
                    </span>
                  </td>

                  <td>
                    <span className="meus-status ativo">
                      {cliente.status || 'ativo'}
                    </span>
                  </td>

                  <td>
                    <div className="meus-acoes">
                      <button
                        className="meus-acao"
                        title="Editar dados do cliente"
                        onClick={() => abrirCliente(cliente)}
                      >
                        <Eye size={16} />
                      </button>

                      {String(cliente.clicksign_status || '').toUpperCase() === 'ASSINADO'
                        ? (
                          <button
                            className="meus-acao assinado"
                            title="Ver documento assinado"
                            onClick={() => abrirAssinatura(cliente)}
                          >
                            <FileSignature size={16} />
                          </button>
                        )
                        : (
                          <button
                            className="meus-acao clicksign"
                            title="Enviar para ClickSign"
                            onClick={() => navigate('/clicksign/contrato-adesao', {
                              state: {
                                client: { ...cliente, _source: 'cpfl' }
                              }
                            })}
                          >
                            <FileSignature size={16} />
                          </button>
                        )}

                      {ehPosVenda && String(cliente.pos_venda_status || '').toUpperCase() === 'ENVIADO' ? (
                        <select
                          className="meus-pos-venda-select"
                          defaultValue=""
                          onChange={(e) => {
                            const valor = e.target.value;
                            if (valor) responderPosVenda(cliente, valor);
                            e.target.value = '';
                          }}
                          title="Registrar devolutiva do Pós-venda"
                        >
                          <option value="">Selecionar</option>
                          <option value="Confirmado e feito ligação">
                            Confirmado e feito ligação
                          </option>
                          <option value="Realizado 1 tentativa sem sucesso">
                            Realizado 1 tentativa sem sucesso
                          </option>
                          <option value="Outro motivo">Outro motivo</option>
                        </select>
                      ) : (
                        <button
                          className={`meus-acao ${podeSolicitarPosVenda(cliente) ? 'pos-venda' : 'disabled'}`}
                          title={
                            podeSolicitarPosVenda(cliente)
                              ? 'Solicitar ligação do Pós-venda'
                              : 'Pós-venda indisponível nesta etapa'
                          }
                          disabled={!podeSolicitarPosVenda(cliente)}
                          onClick={() => solicitarPosVenda(cliente)}
                        >
                          <Phone size={16} />
                        </button>
                      )}

                      <button
                        className={`meus-acao ${
                          String(cliente.pos_venda_status || '').toUpperCase() === 'CONFIRMADO' &&
                          String(cliente.cobranca_status || '').toUpperCase() === 'RASCUNHO'
                            ? 'cobranca'
                            : 'disabled'
                        }`}
                        title="Simular cobrança CPFL"
                        disabled={
                          !(
                            String(cliente.pos_venda_status || '').toUpperCase() === 'CONFIRMADO' &&
                            String(cliente.cobranca_status || '').toUpperCase() === 'RASCUNHO'
                          )
                        }
                        onClick={() => abrirSimulacaoCobranca(cliente)}
                      >
                        <DollarSign size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {clientesFiltrados.length === 0 && (
                <tr>
                  <td colSpan="8" className="meus-clientes-vazio">
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
